/**
 * Rezerwacja konsultacji — kalendarz, wybór godziny i wysyłka formularza.
 *
 * Kalendarz przychodzi z serwera już jako HTML (ten sam szablon Twiga przy
 * pierwszym renderze i przy nawigacji), więc skrypt zajmuje się wyłącznie
 * stanem wyboru, listą godzin i komunikacją z REST API.
 */
document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-booking]').forEach((root) => new BookingWidget(root).init());
});

class BookingWidget {
    constructor(root) {
        this.root = root;
        this.config = BookingWidget.readConfig(root);

        this.flow         = root.querySelector('[data-booking-flow]');
        this.monthsEl     = root.querySelector('[data-booking-months]');
        this.rangeEl      = root.querySelector('[data-booking-range]');
        this.prevBtn      = root.querySelector('[data-booking-prev]');
        this.nextBtn      = root.querySelector('[data-booking-next]');
        this.slotsEl      = root.querySelector('[data-booking-slots]');
        this.dayLabelEl   = root.querySelector('[data-booking-day-label]');
        this.summaryEl    = root.querySelector('[data-booking-summary]');
        this.summaryValue = root.querySelector('[data-booking-summary-value]');
        this.resetBtn     = root.querySelector('[data-booking-reset]');
        this.form         = root.querySelector('[data-booking-form]');
        this.alertEl      = root.querySelector('[data-booking-alert]');
        this.submitBtn    = root.querySelector('[data-booking-submit]');
        this.submitLabel  = root.querySelector('[data-booking-submit-label]');
        this.confirmation = root.querySelector('[data-booking-confirmation]');
        this.dateInput    = root.querySelector('[data-booking-input-date]');
        this.timeInput    = root.querySelector('[data-booking-input-time]');
        this.nonceInput   = root.querySelector('[data-booking-nonce]');

        this.selectedDate = null;
        this.selectedTime = null;
        this.busy = false;
    }

    static readConfig(root) {
        const script = root.parentElement
            ? root.parentElement.querySelector('[data-booking-config]')
            : null;

        try {
            return script ? JSON.parse(script.textContent) : null;
        } catch (error) {
            return null;
        }
    }

    init() {
        if (!this.config || !this.form) return;

        this.days = this.config.days || {};

        this.monthsEl.addEventListener('click', (event) => {
            const cell = event.target.closest('[data-booking-date]');
            if (cell) this.selectDate(cell.dataset.bookingDate);
        });

        this.slotsEl.addEventListener('click', (event) => {
            const slot = event.target.closest('[data-booking-time]');
            if (slot && !slot.disabled) this.selectTime(slot.dataset.bookingTime);
        });

        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.loadMonth(this.config.prev));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.loadMonth(this.config.next));
        if (this.resetBtn) this.resetBtn.addEventListener('click', () => this.clearSelection(true));

        this.form.addEventListener('submit', (event) => this.submit(event));
        this.form.querySelectorAll('.booking__input').forEach((input) => {
            input.addEventListener('input', () => this.clearFieldError(input));
        });

        if (this.config.firstOpen) this.selectDate(this.config.firstOpen);

        this.syncSubmitState();
    }

    // ── Kalendarz ───────────────────────────────────────────────────────────

    loadMonth(fromMonth) {
        if (!fromMonth || this.busy) return Promise.resolve();

        this.busy = true;
        this.root.classList.add('booking--loading');

        const url = new URL(this.config.endpoints.availability);
        url.searchParams.set('from', fromMonth);

        return fetch(url.toString(), { headers: { Accept: 'application/json' } })
            .then((response) => {
                if (!response.ok) throw new Error('availability');
                return response.json();
            })
            .then((view) => {
                this.monthsEl.innerHTML = view.months.map((month) => month.html).join('');
                this.rangeEl.textContent = view.months.map((month) => month.label).join(' — ');

                this.days = view.days || {};
                this.config.from = view.from;
                this.config.prev = view.prev;
                this.config.next = view.next;

                this.prevBtn.disabled = !view.prev;
                this.nextBtn.disabled = !view.next;

                // Wybrany dzień może nie należeć do nowego zakresu — wtedy zaznaczenie znika.
                if (this.selectedDate && !this.days[this.selectedDate]) {
                    this.clearSelection(false);
                } else {
                    this.markSelectedDay();
                }
            })
            .catch(() => {
                this.showAlert('Nie udało się pobrać dostępnych terminów. Spróbuj ponownie.');
            })
            .then(() => {
                this.busy = false;
                this.root.classList.remove('booking--loading');
            });
    }

    selectDate(date) {
        const day = this.days[date];
        if (!day || day.state !== 'open') return;

        this.selectedDate = date;
        this.selectedTime = null;

        this.markSelectedDay();
        this.renderSlots(day);
        this.updateSummary();
        this.syncSubmitState();
    }

    markSelectedDay() {
        this.monthsEl.querySelectorAll('[data-booking-date]').forEach((cell) => {
            const active = cell.dataset.bookingDate === this.selectedDate;
            cell.classList.toggle('booking__day--selected', active);
            cell.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
    }

    // ── Godziny ─────────────────────────────────────────────────────────────

    renderSlots(day) {
        this.dayLabelEl.textContent = this.formatDate(this.selectedDate);

        const slots = (day.slots || []).filter((slot) => slot.available);

        if (slots.length === 0) {
            this.slotsEl.innerHTML = '<p class="booking__slots-empty">Brak wolnych godzin w tym dniu.</p>';
            return;
        }

        this.slotsEl.innerHTML = slots
            .map((slot) => {
                const end = BookingWidget.addMinutes(slot.time, this.config.slotMinutes);
                return `<button type="button" class="booking__slot" data-booking-time="${slot.time}"`
                    + ` aria-label="Godzina ${slot.time} do ${end}">${slot.time}</button>`;
            })
            .join('');
    }

    selectTime(time) {
        this.selectedTime = time;

        this.slotsEl.querySelectorAll('[data-booking-time]').forEach((button) => {
            const active = button.dataset.bookingTime === time;
            button.classList.toggle('booking__slot--selected', active);
            button.setAttribute('aria-pressed', active ? 'true' : 'false');
        });

        this.updateSummary();
        this.syncSubmitState();
    }

    clearSelection(focusCalendar) {
        this.selectedDate = null;
        this.selectedTime = null;

        this.slotsEl.innerHTML = '';
        this.dayLabelEl.textContent = 'Najpierw wybierz dzień w kalendarzu.';

        this.markSelectedDay();
        this.updateSummary();
        this.syncSubmitState();

        if (focusCalendar) {
            const firstOpen = this.monthsEl.querySelector('[data-booking-date]');
            if (firstOpen) firstOpen.focus();
        }
    }

    updateSummary() {
        const complete = Boolean(this.selectedDate && this.selectedTime);

        this.summaryEl.hidden = !complete;

        if (complete) {
            const end = BookingWidget.addMinutes(this.selectedTime, this.config.slotMinutes);
            this.summaryValue.textContent = `${this.formatDate(this.selectedDate)}, ${this.selectedTime}–${end}`;
        }

        this.dateInput.value = this.selectedDate || '';
        this.timeInput.value = this.selectedTime || '';
    }

    syncSubmitState() {
        const ready = Boolean(this.selectedDate && this.selectedTime);

        this.submitBtn.disabled = !ready;
        this.submitBtn.classList.toggle('is-disabled', !ready);
        this.submitLabel.textContent = ready ? 'Zarezerwuj termin' : 'Wybierz termin';
    }

    // ── Wysyłka ─────────────────────────────────────────────────────────────

    submit(event) {
        event.preventDefault();

        if (this.busy || !this.validate()) return;

        this.busy = true;
        this.submitBtn.disabled = true;
        this.root.classList.add('booking--sending');
        this.hideAlert();

        this.post()
            // Nonce z zacache'owanej strony bywa nieaktualny — jedna cicha próba z nowym.
            .then((result) => (result.code === 'bad_nonce'
                ? this.refreshNonce().then((renewed) => (renewed ? this.post() : result))
                : result))
            .then((result) => {
                if (result.ok) {
                    this.showConfirmation(result.booking);
                    return null;
                }

                this.applyErrors(result.errors || {});
                this.showAlert(result.message || 'Nie udało się zapisać rezerwacji.');

                // Zajęty termin znika z kalendarza dopiero po odświeżeniu dostępności.
                if (result.code === 'slot_unavailable' || result.code === 'slot_locked') {
                    this.busy = false;
                    return this.loadMonth(this.config.from);
                }

                return null;
            })
            .catch(() => {
                this.showAlert('Wystąpił błąd połączenia. Spróbuj ponownie za chwilę.');
            })
            .then(() => {
                this.busy = false;
                this.root.classList.remove('booking--sending');
                this.syncSubmitState();
            });
    }

    post() {
        const payload = Object.fromEntries(new FormData(this.form).entries());
        payload.consent = this.form.querySelector('[name="consent"]').checked;

        return fetch(this.config.endpoints.create, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload),
        }).then((response) => response.json());
    }

    refreshNonce() {
        return fetch(this.config.endpoints.session, { headers: { Accept: 'application/json' } })
            .then((response) => {
                if (!response.ok) return false;

                return response.json().then((session) => {
                    this.nonceInput.value = session.nonce;
                    return true;
                });
            })
            .catch(() => false);
    }

    validate() {
        let valid = true;

        const required = [['first_name', 'Podaj imię.'], ['email', 'Podaj prawidłowy adres e-mail.']];
        if (this.config.phoneRequired) required.push(['phone', 'Podaj numer telefonu.']);

        required.forEach(([name, message]) => {
            const input = this.form.querySelector(`[name="${name}"]`);
            const value = input.value.trim();
            const ok = name === 'email' ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) : value.length > 0;

            if (!ok) {
                this.setFieldError(input, message);
                valid = false;
            }
        });

        const consent = this.form.querySelector('[name="consent"]');
        if (!consent.checked) {
            this.setFieldError(consent, 'Wymagana jest zgoda na przetwarzanie danych.');
            valid = false;
        }

        if (!valid) this.showAlert('Uzupełnij zaznaczone pola.');

        return valid;
    }

    applyErrors(errors) {
        Object.keys(errors).forEach((field) => {
            const input = this.form.querySelector(`[name="${field}"]`);
            if (input) this.setFieldError(input, errors[field]);
        });
    }

    setFieldError(input, message) {
        const field = input.closest('[data-booking-field]');
        if (!field) return;

        field.classList.add('booking__field--invalid');

        const target = field.querySelector('[data-booking-error]');
        if (target) target.textContent = message;
    }

    clearFieldError(input) {
        const field = input.closest('[data-booking-field]');
        if (!field) return;

        field.classList.remove('booking__field--invalid');

        const target = field.querySelector('[data-booking-error]');
        if (target) target.textContent = '';
    }

    showConfirmation(booking) {
        const slot = this.root.querySelector('[data-booking-confirm-slot]');
        const reference = this.root.querySelector('[data-booking-confirm-reference]');

        if (slot) slot.textContent = `${booking.date_label} (${booking.day_label}), ${booking.time}–${booking.end}`;
        if (reference) reference.textContent = booking.reference;

        this.flow.hidden = true;
        this.confirmation.hidden = false;
        this.root.classList.add('booking--confirmed');
        this.confirmation.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    showAlert(message) {
        this.alertEl.textContent = message;
        this.alertEl.hidden = false;
    }

    hideAlert() {
        this.alertEl.textContent = '';
        this.alertEl.hidden = true;
    }

    // ── Pomocnicze ──────────────────────────────────────────────────────────

    formatDate(date) {
        const parsed = new Date(`${date}T00:00:00`);

        return parsed.toLocaleDateString(this.config.locale || 'pl-PL', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    }

    static addMinutes(time, minutes) {
        const [hours, mins] = time.split(':').map(Number);
        const total = hours * 60 + mins + minutes;

        return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
    }
}
